// @martinus/scripts - public ESM API.
//
// Successor of v1's window.myApp bundle. Each module is a data-attribute
// driven behavior class over server-rendered HTML; initAll()/destroyAll()
// manage the standard set in one call. The HTML contract (data attributes,
// class names) is unchanged from v1 - only the JS API is new.

import Toggle from './modules/toggle';
import Collapse from './modules/collapse';
import Dropdown from './modules/dropdown';
import Tab from './modules/tab';
import Alert from './modules/alert';
import Input from './modules/input';
import NumberSpinner from './modules/number-spinner';
import RadiocheckToggle from './modules/radiocheck-toggle';
import Tooltip from './modules/tooltip';
import Popover from './modules/popover';
import FeatureHighlight from './modules/feature-highlight';
import Clipboard from './modules/clipboard';
import Modal from './modules/modal';
import Select from './modules/select';
import Carousel from './modules/carousel';
import Gallery from './modules/gallery';
import MegaMenu from './modules/mega-menu';
import Header from './modules/header';
import ScrollSpy from './modules/scroll-spy';
import SmoothScroll from './modules/smooth-scroll';
import AnchorScroll from './modules/anchor-scroll';
import Autocomplete from './modules/autocomplete';
import ThemePicker from './modules/theme-picker';
import ProductPreview from './modules/product-preview';
import AudioPlayer from './modules/audio-player';

import type { ToggleConfig } from './modules/toggle';
import type { CollapseConfig } from './modules/collapse';
import type { DropdownConfig } from './modules/dropdown';
import type { TabConfig } from './modules/tab';
import type { InputConfig } from './modules/input';
import type { NumberSpinnerConfig } from './modules/number-spinner';
import type { RadiocheckToggleConfig } from './modules/radiocheck-toggle';
import type { TooltipConfig } from './modules/tooltip';
import type { PopoverConfig } from './modules/popover';
import type { FeatureHighlightConfig } from './modules/feature-highlight';
import type { ClipboardConfig } from './modules/clipboard';
import type { ModalConfig } from './modules/modal';
import type { SelectConfig } from './modules/select';
import type { CarouselConfig } from './modules/carousel';
import type { GalleryConfig } from './modules/gallery';
import type { MegaMenuConfig } from './modules/mega-menu';
import type { HeaderConfig } from './modules/header';
import type { ScrollSpyConfig } from './modules/scroll-spy';
import type { SmoothScrollConfig } from './modules/smooth-scroll';
import type { AnchorScrollConfig } from './modules/anchor-scroll';
import type { AutocompleteConfig } from './modules/autocomplete';
import type { ThemePickerConfig } from './modules/theme-picker';
import type { ProductPreviewConfig } from './modules/product-preview';
import type { AudioPlayerConfig } from './modules/audio-player';

export { BREAKPOINTS, type Breakpoint } from './const';
export * from './utils';

export {
  Toggle,
  Collapse,
  Dropdown,
  Tab,
  Alert,
  Input,
  NumberSpinner,
  RadiocheckToggle,
  Tooltip,
  Popover,
  FeatureHighlight,
  Clipboard,
  Modal,
  Select,
  Carousel,
  Gallery,
  MegaMenu,
  Header,
  ScrollSpy,
  SmoothScroll,
  AnchorScroll,
  Autocomplete,
  ThemePicker,
  ProductPreview,
  AudioPlayer,
};

export {
  TOGGLE_EVT,
  TRIGGER_EVT,
  doToggle,
  isToggled,
  type ToggleConfig,
  type ToggleEventDetail,
  type DoToggleOptions,
} from './modules/toggle';
export { lockBody, unlockBody, type ModalConfig } from './modules/modal';
// Offcanvas is pure Bootstrap - no wrapper module (v1 parity: main.js exposed
// window.myApp.Offcanvas straight from bootstrap). The re-export gives
// consumers the programmatic handle and evaluating the plugin module
// registers the data-bs-toggle="offcanvas" data API. Per-plugin import, same
// reasoning as in modules/modal.ts.
export { default as Offcanvas } from 'bootstrap/js/dist/offcanvas';
export type { CollapseConfig } from './modules/collapse';
export type { DropdownConfig } from './modules/dropdown';
export type { TabConfig } from './modules/tab';
export type { AlertConfig } from './modules/alert';
export type { InputConfig } from './modules/input';
export type { NumberSpinnerConfig } from './modules/number-spinner';
export type { RadiocheckToggleConfig } from './modules/radiocheck-toggle';
export { SingleTooltip, type TooltipConfig, type TooltipInstance } from './modules/tooltip';
export type { PopoverConfig, PopoverInstance } from './modules/popover';
export type { FeatureHighlightConfig } from './modules/feature-highlight';
export type { ClipboardConfig } from './modules/clipboard';
export {
  attachFloating,
  clearFloating,
  positionFloating,
  type FloatingOptions,
  type Placement,
  type Strategy,
} from './modules/floating';
export type { SelectConfig, SelectLanguage } from './modules/select';
export type { CarouselConfig } from './modules/carousel';
export type {
  GalleryConfig,
  GalleryItem,
  GalleryLanguage,
  GalleryTexts,
} from './modules/gallery';
export type { MegaMenuConfig } from './modules/mega-menu';
export type { HeaderConfig } from './modules/header';
export type { ScrollSpyConfig } from './modules/scroll-spy';
export type { SmoothScrollConfig } from './modules/smooth-scroll';
export type { AnchorScrollConfig } from './modules/anchor-scroll';
export {
  AutocompleteInstance,
  AUTOCOMPLETE_CHANGE_EVT,
  AUTOCOMPLETE_OPEN_EVT,
  AUTOCOMPLETE_CLOSE_EVT,
  AUTOCOMPLETE_ERROR_EVT,
  type AutocompleteConfig,
  type AutocompleteChangeDetail,
  type AutocompleteLanguage,
  type AutocompleteTexts,
} from './modules/autocomplete';
export type { ThemePickerConfig } from './modules/theme-picker';
export {
  ProductPreviewInstance,
  PRODUCT_PREVIEW_CHANGE_EVT,
  type ProductPreviewConfig,
  type ProductPreviewChangeDetail,
  type ProductPreviewLanguage,
  type ProductPreviewTexts,
} from './modules/product-preview';
export {
  AudioPlayerInstance,
  AUDIO_PLAYER_CHANGE_EVT,
  type AudioPlayerConfig,
  type AudioPlayerChangeDetail,
} from './modules/audio-player';

// Theme core - shared with the render-blocking boot snippet (dist/theme.js).
// Exported so a consumer can drive the theme from its own control instead of
// (or alongside) the [data-theme-toggle] button.
export {
  THEME_STORAGE_KEY,
  THEME_CHANGE_EVT,
  THEME_SWITCHING_ATTR,
  DARK_MEDIA_QUERY,
  getSystemTheme,
  getStoredMode,
  getAppliedTheme,
  getTheme,
  resolveTheme,
  applyTheme,
  storeMode,
  setTheme,
  type Theme,
  type ThemeMode,
  type ThemeChangeDetail,
  type ApplyThemeOptions,
} from './theme';

/** Fired on document before initAll() creates instances (IIFE bundle). */
export const INIT_EVT = 'martinus:init';
/** Fired on document after initAll() finished (IIFE bundle). */
export const LOADED_EVT = 'martinus:loaded';

/**
 * Per-module options for initAll(). `false` skips the module entirely; an
 * object is passed to the module constructor as its config.
 */
export interface InitOptions {
  megaMenu?: false | Partial<MegaMenuConfig>;
  toggle?: false | Partial<ToggleConfig>;
  dropdown?: false | Partial<DropdownConfig>;
  tab?: false | Partial<TabConfig>;
  select?: false | SelectConfig;
  modal?: false | Partial<ModalConfig>;
  carousel?: false | CarouselConfig;
  gallery?: false | GalleryConfig;
  productPreview?: false | ProductPreviewConfig;
  collapse?: false | Partial<CollapseConfig>;
  scrollSpy?: false | Partial<ScrollSpyConfig>;
  smoothScroll?: false | Partial<SmoothScrollConfig>;
  tooltip?: false | TooltipConfig;
  popover?: false | PopoverConfig;
  featureHighlight?: false | FeatureHighlightConfig;
  clipboard?: false | Partial<ClipboardConfig>;
  input?: false | Partial<InputConfig>;
  numberSpinner?: false | Partial<NumberSpinnerConfig>;
  radiocheckToggle?: false | Partial<RadiocheckToggleConfig>;
  header?: false | Partial<HeaderConfig>;
  audioPlayer?: false | AudioPlayerConfig;
  anchorScroll?: false | Partial<AnchorScrollConfig>;
  autocomplete?: false | AutocompleteConfig;
  themePicker?: false | Partial<ThemePickerConfig>;
}

/** Instances created by initAll(), keyed by module. */
export interface Instances {
  megaMenu?: MegaMenu;
  toggle?: Toggle;
  dropdown?: Dropdown;
  tab?: Tab;
  select?: Select;
  modal?: Modal;
  carousel?: Carousel;
  gallery?: Gallery;
  productPreview?: ProductPreview;
  collapse?: Collapse;
  scrollSpy?: ScrollSpy;
  smoothScroll?: SmoothScroll;
  tooltip?: Tooltip;
  popover?: Popover;
  featureHighlight?: FeatureHighlight;
  clipboard?: Clipboard;
  input?: Input;
  numberSpinner?: NumberSpinner;
  radiocheckToggle?: RadiocheckToggle;
  header?: Header;
  audioPlayer?: AudioPlayer;
  anchorScroll?: AnchorScroll;
  autocomplete?: Autocomplete;
  themePicker?: ThemePicker;
}

/**
 * Initialize the standard behavior set over the current DOM. Order mirrors
 * v1 main.js. Alert is intentionally not part of the set - it is a
 * programmatic API (`new Alert({...})`), not a DOM-scanned behavior.
 */
export function initAll(options: InitOptions = {}): Instances {
  const instances: Instances = {};

  // MegaMenu must init before Toggle: both listen for clicks on the same
  // triggers (data-mega-menu-trigger + data-toggle="self") and MegaMenu
  // reads the pre-toggle state.
  if (options.megaMenu !== false) {
    instances.megaMenu = new MegaMenu(undefined, options.megaMenu);
  }
  if (options.toggle !== false) {
    instances.toggle = new Toggle(undefined, options.toggle);
  }
  if (options.dropdown !== false) {
    instances.dropdown = new Dropdown(undefined, options.dropdown);
  }
  if (options.tab !== false) {
    instances.tab = new Tab(undefined, options.tab);
  }
  if (options.select !== false) {
    instances.select = new Select(undefined, options.select);
  }
  if (options.modal !== false) {
    instances.modal = new Modal(undefined, options.modal);
  }
  if (options.carousel !== false) {
    instances.carousel = new Carousel(undefined, options.carousel);
  }
  if (options.gallery !== false) {
    instances.gallery = new Gallery(undefined, options.gallery);
  }
  // v1 only exposed ProductPreview (window.myApp.ProductPreview) and left the
  // consumer to `new` it after the AJAX-loaded modal arrived. It is a plain
  // DOM-scanned behavior though, so it joins the standard set: on a page with
  // no preview it creates nothing, and once the modal content lands the
  // consumer calls instances.productPreview.update() instead of constructing.
  if (options.productPreview !== false) {
    instances.productPreview = new ProductPreview(undefined, options.productPreview);
  }
  if (options.collapse !== false) {
    instances.collapse = new Collapse(undefined, options.collapse);
  }
  if (options.scrollSpy !== false) {
    instances.scrollSpy = new ScrollSpy(undefined, options.scrollSpy);
  }
  if (options.smoothScroll !== false) {
    instances.smoothScroll = new SmoothScroll(undefined, options.smoothScroll);
  }
  if (options.tooltip !== false) {
    instances.tooltip = new Tooltip(undefined, options.tooltip);
  }
  if (options.popover !== false) {
    instances.popover = new Popover(undefined, options.popover);
  }
  if (options.featureHighlight !== false) {
    instances.featureHighlight = new FeatureHighlight(undefined, options.featureHighlight);
  }
  if (options.clipboard !== false) {
    instances.clipboard = new Clipboard(undefined, options.clipboard);
  }
  if (options.input !== false) {
    instances.input = new Input(undefined, options.input);
  }
  if (options.numberSpinner !== false) {
    instances.numberSpinner = new NumberSpinner(undefined, options.numberSpinner);
  }
  if (options.radiocheckToggle !== false) {
    instances.radiocheckToggle = new RadiocheckToggle(undefined, options.radiocheckToggle);
  }
  if (options.header !== false) {
    instances.header = new Header(undefined, options.header);
  }
  if (options.audioPlayer !== false) {
    instances.audioPlayer = new AudioPlayer(undefined, options.audioPlayer);
  }
  if (options.anchorScroll !== false) {
    instances.anchorScroll = new AnchorScroll(undefined, options.anchorScroll);
  }
  if (options.autocomplete !== false) {
    instances.autocomplete = new Autocomplete(undefined, options.autocomplete);
  }
  if (options.themePicker !== false) {
    instances.themePicker = new ThemePicker(undefined, options.themePicker);
  }

  return instances;
}

/** Destroy every instance previously created by initAll(). */
export function destroyAll(instances: Instances): void {
  (Object.values(instances) as Array<{ destroy?: () => void }>).forEach(instance => {
    instance?.destroy?.();
  });
}
